import React, { useEffect, useState, CSSProperties, useRef } from 'react';
import { getDownloadFileUrl } from '@api/file-upload-api.ts';
import MediaModal from '@components/media-modal/media-modal.tsx';
import { FileUploadType } from '@models/file-upload.models.ts';
import Avatar from '@mui/material/Avatar';
import Skeleton from '@mui/material/Skeleton';

interface AppImageProps {
  filePath?: string;
  previewSrc?: string;
  type?: FileUploadType;
  fallback?: string;
  disableModalClick?: boolean;
  hideSkeleton?: boolean;
  rounded?: string;
  className?: string;
  style?: CSSProperties;
  width?: number | string;
  height?: number | string;
  variant?: 'image' | 'avatar';
  alt?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  imgClassName?: string;
  onClick?: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onError?: (event?: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  children?: React.ReactNode;
}

const AppImage: React.FC<AppImageProps> = ({
  filePath,
  previewSrc,
  type = FileUploadType.Image,
  fallback = 'N/A',
  disableModalClick = false,
  hideSkeleton = false,
  rounded,
  className = '',
  style,
  width,
  height,
  variant = 'image',
  alt = '',
  objectFit = 'cover',
  imgClassName = '',
  onClick,
  onError,
  children
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(!!filePath && !previewSrc);
  const [error, setError] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const displayedSrc = previewSrc || imageUrl;

  useEffect(() => {
    const loadImage = async () => {
      if (!filePath || previewSrc) return;

      setImageUrl(null);
      setLoading(true);
      setError(false);

      if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        setImageUrl(filePath);
        setLoading(false);
        return;
      }

      try {
        const { url } = await getDownloadFileUrl(filePath);
        setImageUrl(url);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [filePath, previewSrc]);

  const handleClick = (event?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (onClick) return onClick(event);
    if (!disableModalClick && displayedSrc) setShowModal(true);
  };

  const roundedClass = rounded ? `rounded-${rounded}` : '';
  const combinedClass = `${roundedClass} ${className}`.trim();
  const sizeStyle: CSSProperties = { width, height, ...style };

  if (variant === 'avatar') {
    if (loading) {
      return (
        <Skeleton
          variant="circular"
          width={width || 60}
          height={height || 60}
          className={combinedClass}
          animation="wave"
          sx={{ pointerEvents: 'none' }}
        />
      );
    }

    return (
      <Avatar
        src={!error ? (displayedSrc ?? undefined) : undefined}
        alt={alt || fallback}
        onClick={handleClick}
        className={`${combinedClass} ${!disableModalClick ? 'cursor-pointer' : ''}`}
        sx={{
          width,
          height,
          fontSize: typeof width === 'number' ? width / 2.5 : undefined,
          borderRadius: rounded ? 'var(--avatar-radius)' : '50%',
          '& .MuiAvatar-img': {
            width: '100%',
            height: 'auto',
            objectFit: 'contain'
          }
        }}
      >
        {!displayedSrc && children}
      </Avatar>
    );
  }

  // NO IMAGE FALLBACK (no path provided OR error loading image)
  if ((!filePath && !previewSrc) || error) {
    return (
      <div
        className={`flex items-center h-full justify-center bg-gray-100 text-gray-400 text-sm ${combinedClass}`}
        style={sizeStyle}
      >
        {fallback}
      </div>
    );
  }

  // DEFAULT IMAGE VARIANT
  return (
    <>
      <div
        className={`relative h-full overflow-hidden ${combinedClass}`}
        onClick={disableModalClick ? undefined : handleClick}
        style={sizeStyle}
      >
        {loading && !hideSkeleton && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            animation="wave"
            className="absolute inset-0"
            sx={{ pointerEvents: 'none' }}
          />
        )}

        {displayedSrc && (
          <img
            src={displayedSrc}
            alt={alt || fallback}
            style={{
              objectFit,
              width: '100%'
            }}
            className={`h-auto transition-opacity duration-300 ${
              loading ? 'opacity-0' : 'opacity-100'
            } ${roundedClass} ${imgClassName}`}
            onLoad={() => setLoading(false)}
            onError={(event) => {
              setLoading(false);
              setError(true);
              if (onError) onError(event);
            }}
          />
        )}
      </div>

      {displayedSrc && !disableModalClick && (
        <MediaModal
          type={type}
          selectedItem={{ src: displayedSrc }}
          showDialog={showModal}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default AppImage;
